<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import { useAppToast } from '@/shared/lib/useAppToast'
import { usePagination } from '@/shared/composables/usePagination'

import AttributeFormDialog from '@/modules/admin/products/attributes/components/AttributeFormDialog.vue'
import DeleteAttributeConfirmDialog from '@/modules/admin/products/attributes/components/DeleteAttributeConfirmDialog.vue'
import {
  getAllAttributes,
  deleteAttribute as deleteAttr
} from '@/modules/admin/products/attributes/api/attributes.service'

const router = useRouter()
const toast = useAppToast()

const attributes = ref([])
const loading = ref(false)
const searchQuery = ref('')

const filteredAttributes = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return attributes.value
  return attributes.value.filter((a) => a.name?.toLowerCase().includes(q))
})

const isModalOpen = ref(false)
const selectedAttribute = ref(null)

const deleteDialogVisible = ref(false)
const attributeToDelete = ref(null)
const deletingAttributeId = ref(null)

async function loadAttributes() {
  try {
    loading.value = true
    attributes.value = (await getAllAttributes()) || []
  } catch (error) {
    attributes.value = []
    toast.error(error.message || 'Не удалось загрузить атрибуты')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/admin')
}

function goToValues(attr) {
  const attributeId = attr.id ?? attr.Id ?? null

  if (!attributeId) {
    toast.error('Не удалось открыть значения атрибута')
    return
  }

  router.push(`/admin/attributes/${attributeId}/values`)
}

function openCreateModal() {
  selectedAttribute.value = null
  isModalOpen.value = true
}

function openEditModal(attr) {
  selectedAttribute.value = { ...attr }
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  selectedAttribute.value = null
}

async function reloadAttributes() {
  await loadAttributes()
  closeModal()
}

function confirmDelete(attr) {
  attributeToDelete.value = { ...attr }
  deleteDialogVisible.value = true
}

function closeDeleteDialog() {
  if (deletingAttributeId.value) return
  deleteDialogVisible.value = false
  attributeToDelete.value = null
}

async function deleteConfirmed(id) {
  if (deletingAttributeId.value) return
  try {
    if (!id) return

    deletingAttributeId.value = id

    await deleteAttr(id)
    toast.success('Атрибут удалён')

    await loadAttributes()
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении атрибута')
  } finally {
    deletingAttributeId.value = null
    closeDeleteDialog()
  }
}

onMounted(loadAttributes)

const { currentPage, totalPages, paginatedItems: paginatedAttributes } = usePagination(filteredAttributes)
</script>

<template>
  <div class="admin-attributes-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <Button
          label="Добавить атрибут"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateModal"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Атрибуты товаров</h1>
          <p>Управление атрибутами и переход к их значениям</p>
        </div>
      </div>

      <Card class="attributes-card">
        <template #content>
          <div class="search-bar">
            <InputText
              v-model="searchQuery"
              placeholder="Поиск по названию атрибута"
              class="search-input"
            />
          </div>

          <div v-if="loading" class="state-block">
            Загрузка...
          </div>

          <div v-else-if="attributes.length === 0" class="state-block">
            Атрибуты не найдены
          </div>

          <div v-else-if="filteredAttributes.length === 0" class="state-block">
            Ничего не найдено по запросу «{{ searchQuery }}»
          </div>

          <div v-else class="attributes-list">
            <div
              v-for="attr in paginatedAttributes"
              :key="attr.id"
              class="attribute-item"
            >
              <div class="attribute-left">
                <div class="attribute-info">
                  <div class="attribute-name">{{ attr.name }}</div>
                  <div class="attribute-subtitle">
                    Атрибут товара
                  </div>
                </div>
              </div>

              <div class="attribute-actions">
                <Button
                  label="Значения"
                  icon="pi pi-list"
                  class="values-btn"
                  outlined
                  @click="goToValues(attr)"
                />

                <Button
                  icon="pi pi-pencil"
                  class="icon-btn"
                  outlined
                  title="Редактировать"
                  @click="openEditModal(attr)"
                />

                <Button
                  icon="pi pi-trash"
                  class="icon-btn icon-btn--danger"
                  outlined
                  title="Удалить"
                  :loading="deletingAttributeId === attr.id"
                  :disabled="deletingAttributeId === attr.id"
                  @click="confirmDelete(attr)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <AttributeFormDialog
        v-if="isModalOpen"
        :attribute="selectedAttribute"
        @close="closeModal"
        @saved="reloadAttributes"
      />

      <DeleteAttributeConfirmDialog
        :visible="deleteDialogVisible"
        :attribute="attributeToDelete"
        :deleting="deletingAttributeId === (attributeToDelete?.id ?? null)"
        @close="closeDeleteDialog"
        @confirm="deleteConfirmed"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-attributes-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 980px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.attributes-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

.search-bar {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.search-input {
  width: min(480px, 100%);
}

:deep(.attributes-card.p-card) {
  background: #ffffff;
}

:deep(.attributes-card .p-card-content) {
  padding: 0;
}

.state-block {
  margin: 24px;
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.attributes-list {
  display: flex;
  flex-direction: column;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.attribute-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.attribute-item:first-child {
  border-top: none;
}

.attribute-item:hover {
  background: #fcfcfc;
}

.attribute-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.attribute-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.attribute-name {
  font-size: 18px;
  font-weight: 600;
  color: #111111;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.attribute-subtitle {
  font-size: 13px;
  color: #757575;
}

.attribute-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
  align-items: center;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.values-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.values-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.values-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .attribute-item {
    flex-direction: column;
    align-items: stretch;
  }

  .attribute-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .page-header h1 {
    font-size: 30px;
  }
}
</style>